import {
  Body1,
  Body2,
  Box,
  Container,
  Display3,
  Flex,
} from '@sainsburys-tech/fable'
import { AlertCircleFilled } from '@sainsburys-tech/icons'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { useApiClient } from '../../../common/AppContext/appContext'
import BannerHolder from '../../../common/components/Banners/BannerHolder'
import ErrorBanner from '../../../common/components/Banners/ErrorBanner'
import ErrorRefresh from '../../../common/components/ErrorRefresh'
import Header from '../../../common/components/Header'
import ProgressLoading from '../../../common/components/ProgressLoading'
import {
  addBannerMessage,
  setBannerMessages,
} from '../../../common/Context/commonDispatch'
import { Context } from '../../../common/Context/context'
import { headerPlannerTitles } from '../../../common/enums/HeaderItems'
import { Menu } from '../../../common/enums/MenuEnum'
import { PlannerName } from '../../../common/enums/PlannerNameEnum'
import useComponentInitialization from '../../../common/hooks/useComponentInit'
import useErrorHandler from '../../../common/hooks/useErrorHandler'
import { IResult } from '../../../common/types/IResult'
import { toNthDayOfWeekFullDatetimeString } from '../../../common/utils/times'
import { IStackBanner } from '../../../hotfood/types/IStackBanner'
import { getUserStore } from '../../../utils/localStore'
import { getPicklist, updatePicklist } from '../../api/picklistApi'
import { IPicklistItem } from '../../types/IPicklistItem'
import { IPicklistPlannerGroup } from '../../types/IPicklistPlannerGroup'
import GOLPlannerGroupTable from './GOLPlannerGroupTable'

const GOLView = () => {
  const { dispatch, state } = useContext(Context)
  const apiClient = useApiClient()

  const store = useMemo(() => getUserStore(), [])
  const pickListDate = useMemo(() => new Date(), [])

  const [isDataLoading, setIsDataLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [pickListPlannerGroups, setPickListPlannerGroups] = useState<
    IPicklistPlannerGroup[]
  >([])

  const { handleError } = useErrorHandler(setErrorMessage)

  const updateLocalPickList = useCallback(async () => {
    try {
      setIsDataLoading(true)
      const piclist = await getPicklist(apiClient, pickListDate)
      setPickListPlannerGroups(piclist.plannerGroups)
    } catch (e: unknown) {
      handleError('Error loading GOL List', e as Error)
    } finally {
      setIsDataLoading(false)
    }
  }, [apiClient, pickListDate, handleError])

  const { headerNavigationItems, isLoading: initLoading } =
    useComponentInitialization(
      apiClient,
      store,
      dispatch,
      PlannerName.Bakery,
      handleError,
    )

  const isLoading = initLoading || isDataLoading

  useEffect(() => {
    updateLocalPickList()
  }, [updateLocalPickList])

  const saveBanners = (banners: IStackBanner[]) => {
    dispatch(setBannerMessages(banners))
  }

  const handleSkuCheckChanged = (pickListItem: IPicklistItem): void => {
    pickListItem.completed = !pickListItem.completed
    const clone = pickListPlannerGroups.map((x) => ({ ...x }))
    setPickListPlannerGroups(clone)
    updatePicklist(apiClient, {
      completed: pickListItem.completed,
      orderDate: pickListDate,
      sainId: pickListItem.sainId,
      storeId: store.storeId,
    })
      .then((response: IResult) => {
        if (response.isSuccess === false) {
          dispatch(
            addBannerMessage(
              <ErrorBanner
                message={'Error updating GOL List'}
                icon={<AlertCircleFilled className='c-common-error-icon' />}
              />,
            ),
          )

          updateLocalPickList()
        }
      })
      .catch((e: unknown) => {
        if (process.env.NODE_ENV !== 'production') {
          console.log(e)
        }
        dispatch(
          addBannerMessage(
            <ErrorBanner
              message={'Error updating GOL List'}
              icon={<AlertCircleFilled />}
            />,
          ),
        )

        updateLocalPickList()
      })
  }

  const sumQuantityInPlannerGroups = (
    plannerGroups: IPicklistPlannerGroup[],
  ): number => {
    let sum = 0
    plannerGroups.forEach((plannerGroup) => {
      plannerGroup.items.forEach((item) => {
        sum += item.quantity
      })
    })
    return sum
  }

  return (
    <>
      {isLoading && <ProgressLoading message='Loading...' />}

      {!isLoading && (
        <Box backgroundColor='primary' padding='none'>
          <Header
            title={headerPlannerTitles.bakery}
            navItems={headerNavigationItems}
            activeMenuType={Menu.GOL}
          />
          <Container className='c-common-main-view-content'>
            {!isLoading && errorMessage && (
              <ErrorRefresh message={errorMessage} />
            )}

            {!isLoading && !errorMessage && (
              <>
                <BannerHolder
                  banners={state.bannerMessages}
                  setBanners={saveBanners}
                />

                <Display3 style={{ paddingBottom: '16px' }}>
                  GOL Picklist
                </Display3>
                <div>
                  <Body1 style={{ fontWeight: 'normal' }}>
                    {toNthDayOfWeekFullDatetimeString()}
                  </Body1>
                </div>
                <Flex justifyContent='right'>
                  <Body2 data-testid='gol-total-units'>
                    Total units:{' '}
                    {sumQuantityInPlannerGroups(pickListPlannerGroups)}
                  </Body2>
                </Flex>

                {pickListPlannerGroups
                  .sort(
                    (a, b) =>
                      a.plannerGroupPositionOnOutput -
                      b.plannerGroupPositionOnOutput,
                  )
                  .map((plannerGroup: IPicklistPlannerGroup) => (
                    <GOLPlannerGroupTable
                      plannerGroupName={plannerGroup.plannerGroupName}
                      pickList={plannerGroup.items}
                      handleSkuCheckChange={handleSkuCheckChanged}
                    ></GOLPlannerGroupTable>
                  ))}
              </>
            )}
          </Container>
        </Box>
      )}
    </>
  )
}

export default GOLView
